import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ReactApexChart from 'react-apexcharts';
import moment from 'moment';
// @mui
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Card,
    CardHeader,
    Skeleton,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
// components
import { useChart } from '../../../components/chart';
import { fNumber } from '../../../utils/formatNumber';
import i18n from '../../../i18n';

// ----------------------------------------------------------------------

const CHART_HEIGHT = 320;

const LABEL_FORMATS = ['YYYY-MM-DD', 'YYYY-M-D', 'DD.MM.YYYY', 'DD-MM-YYYY', 'YYYY-MM', 'YYYY-M'];

// The labels are days. They used to be cut to "YYYY-MM", which put every day of
// a month on the same x: the bars piled up as 2px slivers and the shared
// tooltip read whichever point it found first. Anything else is left to Date.
const toTime = (label) => {
    const day = moment(String(label), LABEL_FORMATS, true);
    return day.isValid() ? day.valueOf() : new Date(label).getTime();
};

// Trailing windows over what the endpoint returns; "all" is the whole of it.
const PERIODS = [7, 30, 'all'];

/**
 * Payins and payouts per day as grouped columns, with the window picked above
 * the chart. The period is cut from the days the endpoint already sends, so it
 * can never reach further back than that.
 */
export default function DashboardActivityChart({ title, labels, series, loading }) {
    const theme = useTheme();
    const [period, setPeriod] = useState(30);

    // Only the windows shorter than the data are worth offering.
    const periods = PERIODS.filter((p) => p === 'all' || p < labels.length);
    const shown = periods.includes(period) ? period : 'all';
    const from = shown === 'all' ? 0 : Math.max(labels.length - shown, 0);

    const visible = useMemo(
        () => series.map((s) => ({ name: s.name, data: (s.data || []).slice(from) })),
        [series, from]
    );
    const total = visible.reduce(
        (sum, s) => sum + s.data.reduce((acc, v) => acc + (Number(v) || 0), 0),
        0
    );

    const options = useChart({
        chart: { stacked: false },
        colors: [theme.palette.primary.main, theme.palette.warning.main],
        plotOptions: { bar: { columnWidth: '64%', borderRadius: 2 } },
        labels: labels.slice(from).map(toTime),
        // A fixed day format: Apex's own put "Oct '26" on the first tick.
        xaxis: { type: 'datetime', labels: { datetimeUTC: false, format: 'dd MMM' } },
        yaxis: { labels: { formatter: (v) => fNumber(v) } },
        legend: { position: 'top', horizontalAlign: 'right' },
        tooltip: {
            shared: true,
            intersect: false,
            x: { format: 'dd MMM yyyy' },
            y: { formatter: (v) => (v === undefined ? v : fNumber(v)) }
        }
    });

    let content;
    if (loading) {
        content = <Skeleton variant="rounded" height={CHART_HEIGHT} animation="wave" />;
    } else if (!total) {
        content = (
            <Box
                sx={{
                    height: CHART_HEIGHT,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 1,
                    bgcolor: 'background.neutral'
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    {i18n.t('dashboard.noActivity')}
                </Typography>
            </Box>
        );
    } else {
        content = (
            <ReactApexChart type="bar" series={visible} options={options} height={CHART_HEIGHT} />
        );
    }

    return (
        <Card sx={{ height: '100%' }}>
            <CardHeader
                title={title}
                action={
                    periods.length > 1 && (
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={shown}
                            onChange={(event, next) => next !== null && setPeriod(next)}
                            aria-label={i18n.t('dashboard.period.label')}
                        >
                            {periods.map((p) => (
                                <ToggleButton key={p} value={p} sx={{ px: 1.5, py: 0.5 }}>
                                    {p === 'all'
                                        ? i18n.t('dashboard.period.all')
                                        : i18n.t('dashboard.period.days', { days: p })}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                    )
                }
                sx={{ flexWrap: 'wrap', gap: 1 }}
            />
            <Box sx={{ p: 3, pt: 2 }} dir="ltr">
                {content}
            </Box>
        </Card>
    );
}

DashboardActivityChart.propTypes = {
    title: PropTypes.string,
    /** One date label per day, oldest first. */
    labels: PropTypes.arrayOf(PropTypes.string),
    /** `{ name, data }` per series, `data` aligned with `labels`. */
    series: PropTypes.arrayOf(PropTypes.shape({ name: PropTypes.string, data: PropTypes.array })),
    loading: PropTypes.bool
};

DashboardActivityChart.defaultProps = { labels: [], series: [] };
